import { type ChangeEvent, type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { env } from '@/lib/env';

interface CompanyFields {
  companyName: string;
  slug: string;
}

interface AdminFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function RegisterPage(): ReactNode {
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [company, setCompany] = useState<CompanyFields>({ companyName: '', slug: '' });
  const [admin, setAdmin] = useState<AdminFields>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleCompanyChange(e: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = e.target;
    setCompany((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'companyName') {
        next.slug = toSlug(value);
      }
      return next;
    });
  }

  function handleSlugChange(e: ChangeEvent<HTMLInputElement>): void {
    setCompany((prev) => ({ ...prev, slug: e.target.value }));
  }

  function handleAdminChange(e: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = e.target;
    setAdmin((prev) => ({ ...prev, [name]: value }));
  }

  function handleStep1Submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (company.companyName.length < 2) {
      setError('Company name must be at least 2 characters.');
      return;
    }
    // eslint-disable-next-line security/detect-unsafe-regex
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(company.slug)) {
      setError('Slug must be lowercase alphanumeric with hyphens (e.g. acme-tech).');
      return;
    }
    setStep(2);
  }

  async function handleStep2Submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);

    if (admin.password !== admin.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (admin.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${env.VITE_API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: company.companyName,
          slug: company.slug,
          adminEmail: admin.email,
          adminPassword: admin.password,
          adminFirstName: admin.firstName,
          adminLastName: admin.lastName,
        }),
      });

      if (!res.ok) {
        const raw: unknown = await res.json();
        const message =
          raw !== null &&
          typeof raw === 'object' &&
          'message' in raw &&
          typeof (raw as Record<string, unknown>)['message'] === 'string'
            ? String((raw as Record<string, unknown>)['message'])
            : 'Signup failed. Please try again.';
        setError(message);
        return;
      }

      void navigate('/login?registered=1');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Create your company account</CardTitle>
          <CardDescription>
            {step === 1 ? 'Step 1 of 2 — Company details' : 'Step 2 of 2 — Admin account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleStep1Submit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="companyName">Company name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  placeholder="Acme Technologies"
                  value={company.companyName}
                  onChange={handleCompanyChange}
                  required
                  minLength={2}
                  maxLength={100}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="slug">Workspace slug</Label>
                <Input
                  id="slug"
                  name="slug"
                  placeholder="acme-tech"
                  value={company.slug}
                  onChange={handleSlugChange}
                  required
                  minLength={2}
                  maxLength={50}
                />
                {company.slug && (
                  <p className="text-xs text-muted-foreground">Preview: {company.slug}.hrms.app</p>
                )}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full">
                Continue
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <a href="/login" className="underline hover:text-foreground">
                  Sign in
                </a>
              </p>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                void handleStep2Submit(e);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="Priya"
                    value={admin.firstName}
                    onChange={handleAdminChange}
                    required
                    maxLength={50}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Sharma"
                    value={admin.lastName}
                    onChange={handleAdminChange}
                    required
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="priya@acme.com"
                  value={admin.email}
                  onChange={handleAdminChange}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  value={admin.password}
                  onChange={handleAdminChange}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="••••••••"
                  value={admin.confirmPassword}
                  onChange={handleAdminChange}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setError(null);
                    setStep(1);
                  }}
                >
                  Back
                </Button>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Creating account…' : 'Create account'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
